import React, { useState } from 'react';
import useGameStore from '../store/gameStore';
import {
  LEVIERS, PROMO_CONFIG, getProchainesPeriodes, getPeriodeActive,
  computeSegmentsDetail, indexPrixMoyen, defaultMarketing,
} from '../utils/retailMarketing';
import { formatMoney } from '../utils/format';
import TraficDecompoCard from '../components/dashboard/TraficDecompoCard';
import { Megaphone, Star, Percent, CalendarDays, Users2, Zap } from 'lucide-react';
import { toast } from '../components/ui/sonner';

const satColor = (s) => s >= 75 ? 'text-emerald-400' : s >= 50 ? 'text-amber-400' : 'text-red-400';

export default function Marketing() {
  const retail = useGameStore(s => s.retail);
  const totalDays = useGameStore(s => s.totalDays);
  const month = useGameStore(s => s.month);
  const day = useGameStore(s => s.day);
  const toggleLevierMarketing = useGameStore(s => s.toggleLevierMarketing);
  const lancerPromo = useGameStore(s => s.lancerPromo);
  const arreterPromo = useGameStore(s => s.arreterPromo);
  const [promoPct, setPromoPct] = useState(20);
  const [promoDuree, setPromoDuree] = useState(10);

  if (!retail) return <div className="text-slate-400 text-sm" data-testid="marketing-empty">Aucun magasin actif.</div>;
  const mkt = retail.marketing || defaultMarketing();
  const promoActive = mkt.promo && mkt.promo.until > totalDays;
  const periode = getPeriodeActive(month, day);
  const prochaines = getProchainesPeriodes(month, day).slice(0, 4);
  const seg = computeSegmentsDetail(retail);
  const idxPrix = indexPrixMoyen(retail);
  const effets = (mkt.effetsTemporaires || []).filter(e => e.until > totalDays);
  const coutMensuelTotal = LEVIERS.reduce((a, l) => a + (mkt.leviers?.[l.id] ? (l.cout_mensuel || 0) : 0), 0);

  return (
    <div className="space-y-5" data-testid="marketing-page">
      <div>
        <p className="overline">Satisfaction, avis, leviers, promotions & calendrier commercial</p>
        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5 flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-accent" /> Marketing & clients
        </h1>
      </div>

      {/* Satisfaction + avis */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="surface-card p-4" data-testid="carte-satisfaction">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Satisfaction client</p>
          <p className={`text-3xl font-black font-display ${satColor(mkt.satisfaction)}`}>{Math.round(mkt.satisfaction)}<span className="text-sm text-slate-500">/100</span></p>
          <div className="h-1.5 bg-ink-700 rounded-full mt-2 overflow-hidden">
            <div className={`h-full rounded-full ${mkt.satisfaction >= 75 ? 'bg-emerald-500' : mkt.satisfaction >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${mkt.satisfaction}%` }} />
          </div>
          <p className="text-[11px] text-slate-500 mt-2 leading-snug">Montent : accueil, fidélité, promos. Descendent : ruptures, attente en caisse, prix élevés (indice actuel : {idxPrix.toFixed(2)}).</p>
        </div>
        <div className="surface-card p-4" data-testid="carte-avis">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Avis en ligne</p>
          <p className="text-3xl font-black font-display text-foreground flex items-center gap-1.5">
            {mkt.noteAvis.toFixed(1)} <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            <span className="text-xs text-slate-500 font-sans font-normal">({mkt.nbAvis} avis)</span>
          </p>
          <p className="text-[11px] text-slate-500 mt-2 leading-snug">La note suit votre satisfaction avec inertie et pèse sur le trafic de demain : ×0,85 à 1★ → ×1,15 à 5★. Le levier Réseaux sociaux accélère son évolution.</p>
        </div>
        <div className="surface-card p-4" data-testid="carte-budget">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Budget marketing</p>
          <p className="text-3xl font-black font-display text-foreground">{formatMoney(coutMensuelTotal)}<span className="text-sm text-slate-500">/mois</span></p>
          <p className="text-[11px] text-slate-500 mt-2 leading-snug">{Object.values(mkt.leviers || {}).filter(Boolean).length} levier(s) actif(s) · débité chaque jour au prorata.</p>
        </div>
      </div>

      <TraficDecompoCard />

      {/* Leviers */}
      <div className="surface-card p-4" data-testid="leviers-marketing">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-accent" /> Leviers marketing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {LEVIERS.map(l => {
            const actif = !!mkt.leviers?.[l.id];
            const installe = !!mkt.installes?.[l.id];
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => {
                  const res = toggleLevierMarketing(l.id);
                  if (res?.ok && res.installe) toast('Mise en place payée', { description: `${l.nom} : ${formatMoney(l.cout_installation)} débités.` });
                  else if (res?.reason === 'fonds') toast.error('Trésorerie insuffisante pour la mise en place');
                }}
                className={`text-left rounded-lg border p-3 transition-all ${actif ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'}`}
                data-testid={`levier-${l.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-base">{l.emoji}</span>
                  <span className="font-bold text-sm text-slate-100">{l.nom}</span>
                  <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded ${actif ? 'bg-emerald-600/20 text-emerald-400' : 'bg-ink-700 text-slate-500'}`}>{actif ? 'Actif' : 'Inactif'}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">{l.description}</p>
                <p className="text-[11px] font-mono text-slate-300 mt-1.5">
                  {formatMoney(l.cout_mensuel)}/mois
                  {l.cout_installation && !installe ? ` · mise en place ${formatMoney(l.cout_installation)}` : ''}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Promotions */}
      <div className="surface-card p-4" data-testid="bloc-promotions">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-1 flex items-center gap-2"><Percent className="w-4 h-4 text-accent" /> Promotions</h3>
        <p className="text-[11px] text-slate-500 mb-3">Une promo booste le trafic mais rogne la marge (prix réduits sur tout l'assortiment). Trop fréquente, elle crée une <b>habitude</b> : les clients n'y réagissent plus.</p>
        <div className="flex flex-wrap items-center gap-4">
          {promoActive ? (
            <div className="flex flex-wrap items-center gap-3" data-testid="promo-active">
              <span className="bg-accent/15 text-accent border border-accent/40 rounded-full px-3 py-1.5 text-sm font-bold">−{mkt.promo.pct} % en cours · fin J{mkt.promo.until}</span>
              <button type="button" onClick={() => { arreterPromo(); toast('Promotion arrêtée'); }} className="border border-ink-500 text-slate-300 px-3 py-1.5 rounded text-xs font-bold hover:border-red-500 hover:text-red-400" data-testid="arreter-promo">Arrêter la promo</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400 mr-1">Remise :</span>
                {PROMO_CONFIG.paliers_pct.map(p => (
                  <button key={p} type="button" onClick={() => setPromoPct(p)} className={`px-2.5 py-1 rounded text-xs font-bold border ${promoPct === p ? 'bg-accent text-white border-accent' : 'border-ink-600 text-slate-400 hover:border-ink-500'}`} data-testid={`promo-pct-${p}`}>−{p} %</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400 mr-1">Durée :</span>
                {PROMO_CONFIG.durees_jours.map(d => (
                  <button key={d} type="button" onClick={() => setPromoDuree(d)} className={`px-2.5 py-1 rounded text-xs font-bold border ${promoDuree === d ? 'bg-accent text-white border-accent' : 'border-ink-600 text-slate-400 hover:border-ink-500'}`} data-testid={`promo-duree-${d}`}>{d} j</button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  const res = lancerPromo({ pct: promoPct, dureeJours: promoDuree });
                  if (res?.ok) toast('Promotion lancée', { description: `−${promoPct} % sur tout le magasin pendant ${promoDuree} jours.` });
                }}
                className="bg-accent text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-accent-hover transition-colors"
                data-testid="lancer-promo"
              >
                Lancer la promotion
              </button>
            </>
          )}
          <div className="flex items-center gap-2 ml-auto" data-testid="habituation-bar">
            <span className="text-[11px] text-slate-500">Habitude promo</span>
            <div className="w-28 h-1.5 bg-ink-700 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(mkt.habituation / PROMO_CONFIG.habituation_max) * 100}%` }} />
            </div>
            <span className="text-[11px] font-mono text-amber-400">−{Math.round((mkt.habituation || 0) * 100)} % d'effet</span>
          </div>
        </div>
      </div>

      {/* Calendrier commercial */}
      <div className="surface-card p-4" data-testid="calendrier-commercial">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-accent" /> Calendrier commercial</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {prochaines.map(p => {
            const active = periode?.id === p.id;
            return (
              <div key={p.id} className={`rounded-lg border p-3 ${active ? 'border-accent bg-accent/10' : 'border-ink-600 bg-ink-900/40'}`} data-testid={`periode-${p.id}`}>
                <div className="flex items-center gap-2">
                  <span>{p.emoji}</span>
                  <span className="font-bold text-sm text-slate-100">{p.nom}</span>
                  {active && <span className="ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400">En cours</span>}
                </div>
                <p className="text-[11px] font-mono text-slate-400 mt-1">M{p.mois_debut} J{p.jour_debut} → M{p.mois_fin} J{p.jour_fin} · trafic ×{p.trafic_mult}</p>
                {p.attente_promo && <p className="text-[10px] text-amber-400 mt-0.5">Les clients attendent des promos : sans remise, effet réduit (×{PROMO_CONFIG.malus_attente_promo}).</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Segments */}
      <div className="surface-card p-4" data-testid="segments-clients">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-1 flex items-center gap-2"><Users2 className="w-4 h-4 text-accent" /> Segments clients</h3>
        <p className="text-[11px] text-slate-500 mb-3">Attractivité globale : <b className="font-mono text-slate-200">×{seg.mult.toFixed(2)}</b> — chaque segment réagit à vos prix (indice {idxPrix.toFixed(2)}), à votre service (satisfaction) et à la largeur de votre assortiment.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {seg.detail.map(({ segment: sgt, prixResp, serviceResp, choixResp, mult }) => (
            <div key={sgt.id} className="rounded-lg border border-ink-600 bg-ink-900/40 p-3" data-testid={`segment-${sgt.id}`}>
              <div className="flex items-center gap-2">
                <span className="text-base">{sgt.emoji}</span>
                <span className="font-bold text-sm text-slate-100">{sgt.nom}</span>
                <span className="ml-auto text-[10px] font-mono text-slate-500">{sgt.poids_pct} %</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">{sgt.description}</p>
              <div className="mt-2 text-[11px] font-mono space-y-0.5">
                <p className={prixResp >= 1 ? 'text-emerald-400' : 'text-red-400'}>Prix ×{prixResp.toFixed(2)}</p>
                <p className={serviceResp >= 1 ? 'text-emerald-400' : 'text-red-400'}>Service ×{serviceResp.toFixed(2)}</p>
                <p className={choixResp >= 1 ? 'text-emerald-400' : 'text-red-400'}>Choix ×{choixResp.toFixed(2)}</p>
                <p className="text-slate-200 font-bold pt-0.5 border-t border-ink-700">Attractivité ×{mult.toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Effets temporaires (événements) */}
      {effets.length > 0 && (
        <div className="surface-card p-4" data-testid="effets-evenements">
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-2">Effets d'événements en cours</h3>
          <div className="space-y-1.5">
            {effets.map((e, i) => (
              <p key={i} className="text-xs font-mono text-slate-300 flex flex-wrap gap-2 items-center">
                <span className="font-sans font-bold text-slate-100">{e.label}</span>
                {e.traficMult !== 1 && <span className={e.traficMult > 1 ? 'text-emerald-400' : 'text-red-400'}>trafic ×{e.traficMult}</span>}
                {e.conversionMult !== 1 && <span className={e.conversionMult > 1 ? 'text-emerald-400' : 'text-red-400'}>conversion ×{e.conversionMult}</span>}
                {e.surcoutPctCa > 0 && <span className="text-red-400">surcoût {e.surcoutPctCa} % CA</span>}
                <span className="text-slate-500">· jusqu'au J{e.until}</span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
